// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFieldPropsSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import 'share/utils/Izenda.StringUtilities';

import constants from 'logic/data/Izenda.ReportBodyConstants';
import {
  REPORT_PART_TYPE,
  BUNDLE,
  DRIL_DOWN_STYLE_OPTIONS,
  DRIL_DOWN_STYLE,
  PAGE_NAME
} from 'logic/data/Izenda.ReportDesignerConstants';
import SCHEMA_DEF from 'logic/data/Izenda.ReportPartFieldPropsSchema';
import {
  FIELD_FUNCTION_MODE,
  FUNCTION as FUNCTION_CONFIG,
  MESSAGES as FIELDS_MESSAGES,
  EMPTY_FUNCTION,
  ERROR_TYPE,
  SORT_ORDERS
} from 'logic/data/Izenda.ReportPartFieldConstants';
import { ContainerName, SORT_TYPES } from 'logic/data/Izenda.ReportPartChartConstants';
import commonDataStore from 'logic/Izenda.ReportDesignerCommonDataStore';
import { action, toJS, asReference } from 'mobx';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import reportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import ObjectUtils from 'share/utils/Izenda.ObjectUtils';
import { SUB_REPORT, DEFAULT_PROPERTIES_VALUE } from 'logic/data/Izenda.PropertiesBuilderConstants';
import uiState from 'logic/Izenda.UiState';
import localizer from 'share/localization/Izenda.Localizer';
import ReportPartElementUtils from 'share/utils/Izenda.ReportPartElementUtils';
import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
import DateTimeUtils from 'share/utils/Izenda.DateTimeUtils';
import { DATE_TIME_FORMAT } from 'logic/data/Izenda.TimeFormatConstants';
import Panel from 'share/components/controls/Izenda.Panel';
import FontStyle from 'share/components/controls/Izenda.FontStyle';
import FontFormat from 'share/components/controls/Izenda.FontFormat';
import ButtonHighlight from 'share/components/controls/Izenda.ButtonHighlight';
import SizeSetting from 'share/components/controls/Izenda.SizeSetting';
import ColorStyling from 'share/components/controls/Izenda.ColorStyling';
import AlignmentSetting from 'share/components/controls/Izenda.AlignmentSetting';
import TextCellColorSettings from 'share/components/controls/Izenda.TextCellColorSettings';
import CalculatedFieldProperties from 'share/components/controls/Izenda.CalculatedFieldProperties';
import CustomInput from 'share/components/controls/Izenda.CustomInput';
import VerticalAlignmentSetting from 'share/components/controls/Izenda.VerticalAlignmentSetting';
import commonMessages from 'share/messages/Izenda.CommonMessages';

import {
  getOr,
  get,
  cloneDeep,
  isNil,
  isEmpty,
  isEqual,
  omit,
  map,
  includes,
  filter,
  invoke,
  find,
  isUndefined,
  assignWith,
  invokeArgs,
  constant,
  concat,
  pick
} from 'lodash/fp';

const MESSAGES = BUNDLE.MESSAGE;
const ICON_TYPES = SUB_REPORT.ICON_TYPES;

const settingByRangeDefaultObj = {
  rangePercent: null,
  rangeValue: null,
  value: null,
  allValues: null
};

const WIDGETS = {
  Panel,
  FontStyle,
  FontFormat,
  ButtonHighlight,
  SizeSetting,
  ColorStyling,
  AlignmentSetting,
  VerticalAlignmentSetting,
  TextCellColorSettings,
  CalculatedFieldProperties,
  CustomInput
};

const FILED_OPTIONS = {
  FormatKey: 'format',
  FunctionGroup: 'Group'
};

export default function(editorComp, reportDesignerModel, fieldStore, itemProps) {
  const {
    configField = {},
    authorization,
    authorizationConstants: { PERMISSION_KEY }
  } = itemProps;
  const configReportPart = reportDesignerModel.configReportPart || {};
  const reportFields = reportDesignerModel.reportFields || {};
  const reportFilters = reportFields.reportFilter || {};
  const reportPartContent = configReportPart.reportPartContent || {};
  const containerName = get('fieldContainerDetails.name', itemProps);

  ensure(!!reportDesignerModel, 'reportDesignerModel must be set');

  return {
    // define data to render UI
    definedSchema: cloneDeep(SCHEMA_DEF),

    UPDATE_DATA_BY_KEY_CHANGE: [
      'function',
      'subTotal',
      'grandTotal',
      'format',
      'sort',
      'fieldNameAlias',
      'fieldItemVisible',
      'embeddedJavascript'
    ],

    // list of widgets which are used to render UI
    widgets: WIDGETS,

    // data to binding to fields
    source: configField,

    // properties for different fields
    mappingProps: {
      fieldStore: itemProps.fieldStore,

      stypeOptions: DRIL_DOWN_STYLE_OPTIONS,

      changeFieldNameAlias(newValue, oldAlias) {
        // Update new alias to agrregated field
        reportFilters.updateAggregatedFieldReportFieldAlias(oldAlias, newValue.value, configReportPart.headerTitle);

        configReportPart.updateContentWithFieldName &&
          configReportPart.updateContentWithFieldName(oldAlias, newValue.value);
      },

      beforeChangeFormat(newVal, otherInfo) {
        // Reset data in Report Part Element Properties
        const newFormat = getOr({}, get('format', otherInfo), DATE_TIME_FORMAT);
        const oldFormat = getOr({}, get('fieldFormatData', otherInfo), DATE_TIME_FORMAT);

        newFormat.INTERVAL_VALUE !== oldFormat.INTERVAL_VALUE &&
          configField.properties.changeFunctionFormatAction &&
          configField.properties.changeFunctionFormatAction();

        if (newVal && isNil(newVal.value)) {
          const originalArguments = arguments;

          return reportPartUtils.getDefaultFormat(configField).then(defaultFormatInfo => {
            return defaultFormatInfo ? [{ value: get('id', defaultFormatInfo) }, defaultFormatInfo] : originalArguments;
          });
        }
        return Promise.resolve(arguments);
      },

      beforeChangeFunction(newVal, otherInfo) {
        const newFunctionId = getOr(null, 'value', newVal);
        const newFunctionName = getOr(null, 'name', otherInfo);
        const container = itemProps.fieldContainerDetails;
        const originalArguments = arguments;
        const validateResult = reportPartContent.validateFunction(container, otherInfo);
        const confirmMessage = itemProps.confirmMessage;

        // Reset data in Report Part Element Properties
        configField.properties.changeFunctionFormatAction && configField.properties.changeFunctionFormatAction();

        const checkAggregatedFields = itemProps => {
          const configField = itemProps.configField;
          const filterFields = itemProps.fieldStore.reportFilters.filterFields;

          if (configField.isAggregatedField) {
            const aggregatedFields = filter(function(field) {
              return !isNil(field.reportPartTitle);
            }, filterFields);

            if (!isEmpty(aggregatedFields)) {
              return itemProps.confirmFn('Confirmation', {
                description: constants.MESSAGES.M06
              });
            }
          }
          return Promise.resolve();
        };

        const getDefaultFunctionFormat = (reportPartContent, container, configField) => {
          return reportPartUtils.getDefaultFunctionFormat(reportPartContent, container, configField).then(data => {
            let updateChangedOthersInfo = data.functionData;
            const defaultFunctionConfig = data.defaultFunctionConfig || {};
            // this field does not accept empty function

            if (defaultFunctionConfig.ON_NULL_FUNCTION === ERROR_TYPE.ERROR_MESSAGE) {
              confirmMessage('error', String.format(FIELDS_MESSAGES.M10, configField.field.fieldName));
            } else if (defaultFunctionConfig.ON_NULL_FUNCTION === ERROR_TYPE.NOTIFY) {
              // this field accept empty function but warning message
              confirmMessage('error', FIELDS_MESSAGES.M08);
            }

            if (updateChangedOthersInfo) {
              updateChangedOthersInfo.defaultFormat = data.formatData
                ? {
                    formatId: data.formatData.id,
                    format: data.formatData.format,
                    groupBy: data.formatData.groupBy,
                    formatDataType: data.formatData.formatDataType
                  }
                : '';
              return [
                {
                  value: updateChangedOthersInfo.id
                },
                updateChangedOthersInfo
              ];
            }

            return originalArguments;
          });
        };

        return checkAggregatedFields(itemProps).then(() => {
          if (!validateResult.status && validateResult.useDefaultFunction) {
            return getDefaultFunctionFormat(reportPartContent, container, configField);
          }
          let functionInfo = originalArguments[1];
          const originalValue = originalArguments[0];

          return reportPartUtils.getDefaultFormat(configField, functionInfo).then(defaultFormatInfo => {
            if (defaultFormatInfo) {
              functionInfo = functionInfo || {};
              functionInfo.defaultFormat = {
                formatId: defaultFormatInfo.customId,
                format: defaultFormatInfo.format,
                groupBy: defaultFormatInfo.groupBy,
                formatDataType: defaultFormatInfo.formatDataType
              };
            }

            // Update cell color threshold settings:
            reportPartUtils.updateThresholdFunction(configField, newFunctionId, newFunctionName, reportPartContent);

            return [originalValue, functionInfo];
          });
        });
      },

      beforeChangeSorting(newVal, otherInfo) {
        if (ObjectUtils.isBinary(configField.field.dataFieldType)) {
          itemProps.confirmMessage('error', constants.MESSAGES.M08);
          return Promise.reject();
        }
        return Promise.resolve();
      },

      afterOnChangeSubTotal(calculatedData, sortOrderObject) {
        if (calculatedData.functionName) {
          configField.properties.setSortValue(sortOrderObject.value);
        }
      },

      canUnsort() {
        return reportPartContent.checkCanUnsort && reportPartContent.checkCanUnsort(itemProps.fieldContainerDetails);
      },

      resetDefaultAlternativeText() {
        configField.resetDefaultAlternativeText && configField.resetDefaultAlternativeText();
        // update html for form after a changed of field property.
        reportPartContent.updateHTMLCodeFieldContent && reportPartContent.updateHTMLCodeFieldContent(configField);
      },
      resetDefaultCustomURL() {
        configField.resetDefaultCustomURL && configField.resetDefaultCustomURL();
        // update html for form after a changed of field property.
        reportPartContent.updateHTMLCodeFieldContent && reportPartContent.updateHTMLCodeFieldContent(configField);
      },
      resetDefaultSubReport() {
        configField.resetDefaultSubReport && configField.resetDefaultSubReport();
        // update html for form after a changed of field property.
        reportPartContent.updateHTMLCodeFieldContent && reportPartContent.updateHTMLCodeFieldContent(configField);
      },
      resetDefaultEmbeddedJavascript() {
        configField.resetDefaultEmbeddedJavascript && configField.resetDefaultEmbeddedJavascript();
        // update html for form after a changed of field property.
        reportPartContent.updateHTMLCodeFieldContent && reportPartContent.updateHTMLCodeFieldContent(configField);
      },
      resetDefaultSubTotal: action(() => {
        configField.resetDefaultSubTotal && configField.resetDefaultSubTotal();
        // update html for form after a changed of field property.
        reportPartContent.updateHTMLCodeFieldContent && reportPartContent.updateHTMLCodeFieldContent(configField);
        // get new report part review data
        configReportPart.shouldUpdatePreview && itemProps.fieldStore.needUpdatePreview(configReportPart);
      }),
      resetDefaultGrandTotal: action(() => {
        configField.resetDefaultGrandTotal && configField.resetDefaultGrandTotal();
        // update html for form after a changed of field property.
        reportPartContent.updateHTMLCodeFieldContent && reportPartContent.updateHTMLCodeFieldContent(configField);
        // get new report part review data
        configReportPart.shouldUpdatePreview && itemProps.fieldStore.needUpdatePreview(configReportPart);
      }),
      fnResetTextDefaultValue() {
        configField.resetTextDefaultValue && configField.resetTextDefaultValue();
        // update html for form after a changed of field property.
        reportPartContent.updateHTMLCodeFieldContent && reportPartContent.updateHTMLCodeFieldContent(configField);
      },
      fnResetCellDefaultValue() {
        configField.resetCellDefaultValue && configField.resetCellDefaultValue();
        // update html for form after a changed of field property.
        reportPartContent.updateHTMLCodeFieldContent && reportPartContent.updateHTMLCodeFieldContent(configField);
      },
      hasSettingAlternativeText() {
        return !isEqual(
          toJS(configField.properties.dataFormattings.alternativeText),
          DEFAULT_PROPERTIES_VALUE.dataFormattings.alternativeText
        );
      },
      getAlternativeText() {
        const alternativeText =
          toJS(configField.properties.dataFormattings.alternativeText) ||
          DEFAULT_PROPERTIES_VALUE.dataFormattings.alternativeText;

        return alternativeText;
      },
      getCustomUrl() {
        const customURL = getOr({}, 'properties.dataFormattings.customURL', configField);
        return pick(['url', 'option', 'selectedIconValue', 'viewSettingByLink'], customURL);
      },
      hasSettingCustomURL() {
        return !!configField.properties.dataFormattings.customURL.url;
      },
      getEmbeddedScript() {
        const embeddedJavascript = getOr({}, 'properties.dataFormattings.embeddedJavascript', configField);
        return pick(['script', 'selectedIconValue', 'viewSettingByLink'], embeddedJavascript);
      },
      hasSettingEmbeddedJavascript() {
        return !!configField.properties.dataFormattings.embeddedJavascript.script;
      },
      getSubReport() {
        const subReport = getOr({}, 'properties.drillDown.subReport', configField);
        return pick(
          [
            'style',
            'selectedReport',
            'reportPartUsed',
            'reportFilter',
            'mappingFields',
            'selectedIconValue',
            'viewSettingByLink'
          ],
          subReport
        );
      },
      hasSettingSubReport() {
        const subReport = getOr({}, 'properties.drillDown.subReport', configField);

        return !!subReport.selectedReport;
      },
      hasSettingSubTotal() {
        const subTotal = getOr({}, 'properties.dataFormattings.subTotal', configField);

        return !isEmpty(subTotal.label);
      },
      hasSettingGrandTotal() {
        const grandTotal = getOr({}, 'properties.dataFormattings.grandTotal', configField);
        return !isEmpty(grandTotal.label);
      },
      getDataSubTotal() {
        let subTotalData = configField.properties.dataFormattings.subTotal;

        subTotalData.querySourceId = configField.field.querySouceId;
        subTotalData.fieldDataType = configField.field.dataFieldType;
        subTotalData.previewRecord = reportDesignerModel.previewRecord;

        return subTotalData;
      },

      getDataGrandTotal() {
        let grandTotalData = configField.properties.dataFormattings.grandTotal;

        grandTotalData.querySourceId = configField.field.querySouceId;
        grandTotalData.fieldDataType = configField.field.dataFieldType;
        grandTotalData.previewRecord = reportDesignerModel.previewRecord;

        return grandTotalData;
      },
      getDataTypeTextCellColor() {
        return {
          dataType: get('previewDataType', configField),
          dateTimeFormatData: get('dateTimeFormatData', configField),
          settingValue: toJS(configField.properties.dataFormattings.color)
        };
      },
      getDateTimeFormatData() {
        return get('dateTimeFormatData', configField);
      },
      getSortValue() {
        return configField.properties.dataFormattings;
      },
      getWordWrapValue() {
        return configField.properties.headerFormating;
      },
      isEmptySortValue(val) {
        // sort buttons can't be in "active" state.
        return true;
      },
      getSortList() {
        if (
          reportPartContent.checkCanUnsort &&
          !reportPartContent.checkCanUnsort(itemProps.fieldContainerDetails, itemProps.configField)
        ) {
          return map(omit(['headerIcon']), [SORT_ORDERS.ASC, SORT_ORDERS.DESC]);
        } else {
          return map(omit(['headerIcon']), [SORT_ORDERS.Unsorted, SORT_ORDERS.ASC, SORT_ORDERS.DESC]);
        }
      }
    },

    // defined-function which fields need to execute to get its data
    mappingSource: {
      getWidthValue() {
        return configField.properties.dataFormattings;
      },
      getHeightValue() {
        return configField.properties.headerFormating;
      },
      getFieldName() {
        return configField.field && configField.field.visible
          ? configField.field.fieldName
          : constants.FIELD_HIDDEN_LABEL;
      },

      getFieldNameAlias() {
        return configField.field && configField.field.visible
          ? configField.field.fieldNameAlias
          : constants.FIELD_HIDDEN_LABEL;
      },

      getDataType() {
        return configField.previewDataType;
      },

      isHiddenFunction() {
        const dataFieldType = (configField && configField.getValueByKey('dataFieldType')) || {};

        // We do not show function component when range option of chart is not null
        if (
          configReportPart.reportPartContent.type === REPORT_PART_TYPE.Chart &&
          includes(configReportPart.reportPartContent.chartType, [
            CHART_TYPE.Column,
            CHART_TYPE.Area,
            CHART_TYPE.Bar
          ]) &&
          configReportPart.reportPartContent.properties.optionByType &&
          configReportPart.reportPartContent.properties.optionByType.izRange &&
          itemProps.fieldContainerDetails.name === 'values'
        ) {
          return true;
        }

        // hide functionf field-data-type is LargerObject (CLOB or BLOB or Image)
        return dataFieldType === 'LargerObject';
      },

      isVerticalHeaderHeightHidden() {
        return !getOr(false, 'properties.headerFormating.verticalHeader', configField);
      },

      isHiddenCustomURL() {
        return authorization && !authorization.checkPrivileges([PERMISSION_KEY.ReportsFieldPropertiesCustomURL]);
      },

      isHiddenEmbeddedJavascript() {
        return (
          authorization && !authorization.checkPrivileges([PERMISSION_KEY.ReportsFieldPropertiesEmbeddedJavaScript])
        );
      },

      hiddenByReportPartType() {
        return reportPartContent.type !== REPORT_PART_TYPE.Form;
      },

      isHiddenSubReport() {
        return authorization && !authorization.checkPrivileges([PERMISSION_KEY.ReportsFieldPropertiesSubreport]);
      },

      isHiddenSort() {
        // Hide sort in y-Axis for linear type
        let hideSortForValue = false;
        if (reportPartContent.type === REPORT_PART_TYPE.Chart) {
          hideSortForValue = reportPartContent.isLinear() && containerName === ContainerName.VALUES;
        }

        return (
          configField.field.isRunningField ||
          configField.hiddenSortFeature ||
          !!invokeArgs(
            'checkHiddenSortField',
            [configField, itemProps.fieldContainerDetails],
            configReportPart.reportPartContent
          ) ||
          hideSortForValue
        );
      },

      getFunctions() {
        function filterData(data) {
          data = concat([EMPTY_FUNCTION], data);

          if (!reportPartContent.checkedCompatibleFunction) {
            return data;
          }

          return filter(
            reportPartContent.checkedCompatibleFunction.bind(
              reportPartContent,
              itemProps.fieldContainerDetails,
              configField
            ),
            data
          );
        }

        const querySourceId = get('querySourceId', configField.field);

        return commonDataStore
          .loadReportFunction(
            FIELD_FUNCTION_MODE.FieldProperty,
            configField.getValueByKey('dataFieldType'),
            uiState.tenantId,
            [querySourceId]
          )
          .then(filterData);
      },

      getDataFormats() {
        const functionInfo = configField.getValueByKey('functionInfo') || {};
        const dataType = functionInfo.formatDataType || configField.getValueByKey('dataFieldType');

        return commonDataStore.loadReportFieldDataFormat(dataType).then(formats => {
          if (dataType === DATA_TYPES.DATE_TIME) {
            // filter date time format that current user setting can supported
            return DateTimeUtils.filterDateTimeFormat(formats);
          }
          if (dataType === DATA_TYPES.MONEY || dataType === DATA_TYPES.NUMERIC) {
            return reportPartUtils.filterFormatForField(
              reportPartContent,
              formats,
              configField.field.isRunningField,
              containerName
            );
          }

          return formats;
        });
      },

      getDataFormatsWidth() {
        return configField.properties.getDataValueByKey('width').value;
      },

      getVerticalHeaderHeight() {
        return configField.properties.getHeaderValueByKey('verticalHeaderHeight').value;
      },

      getDataFormatType() {
        const format = configField.getValueByKey('format') || {};

        return format.formatId;
      },

      getDataFontFormat() {
        const fontConfig = configField.properties.dataFormattings.font || {};

        return {
          fontFamily: fontConfig.family,
          fontSize: fontConfig.size
        };
      },

      getDataFontStyle() {
        const fontConfig = configField.getValueByKey('font') || {};

        return {
          fontUnderline: fontConfig.underline,
          fontItalic: fontConfig.italic,
          fontBold: fontConfig.bold
        };
      },

      getHeaderFontFormat() {
        const fontConfig = configField.properties.headerFormating.font || {};

        return {
          fontFamily: fontConfig.family,
          fontSize: fontConfig.size
        };
      },

      getHeaderFontStyle() {
        const fontConfig = configField.properties.headerFormating.font || {};

        return {
          fontUnderline: fontConfig.underline,
          fontItalic: fontConfig.italic,
          fontBold: fontConfig.bold
        };
      },

      getDataAlignmentFormat() {
        return configField.properties.dataFormattings.alignment;
      },

      getDataWordWrap() {
        return configField.properties.dataFormattings.wordWrap;
      },

      getHeaderAlignmentFormat() {
        return configField.properties.headerFormating.alignment;
      },

      getHeaderVerticalAlignmentFormat() {
        return configField.properties.headerFormating.verticalAlignment;
      },

      getDataFormattingVerticalAlignmentFormat() {
        return configField.properties.dataFormattings.verticalAlignment;
      },

      getFieldStore() {
        return itemProps.fieldStore;
      },

      getSettingDataType() {
        return get('previewDataType', configField);
      },

      getHeaderColorStyling() {
        return {
          textColor: configField.properties.headerFormating.font.color,
          backgroundColor: configField.properties.headerFormating.font.backgroundColor
        };
      },

      hiddenIconSettingSubReport() {
        const type = configReportPart.reportPartContent.type;

        return includes(type, [REPORT_PART_TYPE.Chart, REPORT_PART_TYPE.Gauge, REPORT_PART_TYPE.Map]);
      },

      checkUniqLinkOption() {
        const properties = configField.properties || {};

        return properties.checkUniqLinkOption.bind(properties);
      },

      checkEmbeddedLinkOption() {
        const properties = configField.properties || {};

        return properties.checkEmbeddedLinkOption.bind(properties);
      },

      getAliasValidation() {
        return {
          presence: {
            presence: true,
            message: localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_FIELD_NAME_ALIAS_MESSAGE_REQUIRED')
          },
          action: asReference(() => {
            return function validateAlias(newName) {
              const isExisting =
                configReportPart.checkExistingFieldNameAlias &&
                configReportPart.checkExistingFieldNameAlias(configField, newName);
              if (isExisting) {
                return Promise.resolve(String.format(MESSAGES.M09, reportDesignerModel.configReportPart.headerTitle));
              }

              // Field name can not contain '[', ']' character
              const invalidName = newName.indexOf('[') > -1 || newName.indexOf(']') > -1;
              if (invalidName) {
                return Promise.resolve(localizer.getText('CALCULATED_FIELD_MESSAGE_INVALID_NAME'));
              }

              return Promise.resolve('');
            };
          })
        };
      },

      /**
       * Get field setting alias
       * @return {String} field name alias
       */
      getFieldSetting() {
        return getOr('', 'field.fieldNameAlias', configField);
      },

      hiddenAlternativeTextByReportPartType() {
        return reportPartContent.type === REPORT_PART_TYPE.Grid || reportPartContent.type === REPORT_PART_TYPE.Map;
      },

      hiddenColorByReportPartType() {
        return reportPartContent.type === REPORT_PART_TYPE.Grid;
      },

      isGridReport() {
        return reportPartContent.type === REPORT_PART_TYPE.Grid;
      },

      isRunningField() {
        return get('field.isRunningField', configField);
      }
    },

    onChangeValue: action('OnChangedFieldProps', function(
      schemaData,
      changedKey,
      changedKeyPath,
      changedValue,
      changedOthersInfo
    ) {
      const groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
      const fieldData = groupData[changedKey];

      fieldData && (fieldData.value = changedValue);

      // flag to check whether need to reload percentage-data to use for
      // setting: alternative text, text color, cell color...(range percent)
      const beforeUpdateNeedPercentageData = invoke('needPercentageData', configField.properties);

      const needUpdateChangeKey = this.UPDATE_DATA_BY_KEY_CHANGE || [];
      const oldAlias = configField.name;

      const dataFormattings = getOr({}, 'dataFormattings', schemaData) || {};
      const dataFormattingsFontFormat = getOr({}, 'fontFormat.value', dataFormattings) || {};
      const dataFormattingsFontStyle = getOr({}, 'fontStyle.value', dataFormattings) || {};
      let dataFormattingsAlternativeText = getOr({}, 'alternativeText.value', dataFormattings) || {};
      let customURL = getOr({}, 'customURL.value', dataFormattings);
      let embeddedJavascript = getOr({}, 'embeddedJavascript.value', dataFormattings);
      const headerFormating = getOr({}, 'headerFormating', schemaData) || {};
      const headerFormatingFontFormat = getOr({}, 'fontFormat.value', headerFormating) || {};
      const headerFormatingFontStyle = getOr({}, 'fontStyle.value', headerFormating) || {};
      const drillDown = getOr({}, 'drillDown', schemaData) || {};
      const subreport = getOr({}, 'subReport', drillDown) || {};
      const subReportValue = getOr({}, 'value', subreport) || {};
      const dataSource = getOr({}, 'dataSource', schemaData) || {};
      const headerFormatingColor = getOr({}, 'color.value', headerFormating) || {};
      const grandTotalData = getOr({}, 'grandTotal.value', dataFormattings) || {};
      const subTotalData = getOr({}, 'subTotal.value', dataFormattings) || {};
      const otherProps = getOr({}, 'otherProps', schemaData) || {};
      let dataFormattingsWidth = get('width.value', dataFormattings);
      let verticalHeaderHeight = get('verticalHeaderHeight.value', headerFormating);

      if (changedKey !== 'width') {
        const width = get('width', dataFormattings);
        const values = getOr('props.values', width);
        values && (dataFormattingsWidth = get(values.propertyKey, values.loadValue));
      }

      if (changedKey !== 'verticalHeaderHeight') {
        const height = get('verticalHeaderHeight', dataFormattings);
        const values = getOr('props.values', height);
        values && (verticalHeaderHeight = get(values.propertyKey, values.loadValue));
      }

      const isChangeFunction = changedKey === 'function';

      if (changedKey === 'customURL' || changedKey === 'embeddedJavascript' || changedKey === 'subReport') {
        if (get('url', customURL) || get('selectedReport', subreport) || get('script', embeddedJavascript)) {
          reportPartContent.enableValueLabel && reportPartContent.enableValueLabel();
        }
      }

      if (changedKey === 'subReport' && get('style.value', subreport) === DRIL_DOWN_STYLE.EMBEDDED) {
        switchToUseIconOptionInHyperlink();
      }

      const fieldInfo = {
        fieldNameAlias:
          changedKey === 'fieldNameAlias' || isChangeFunction
            ? get('fieldNameAlias.value', dataSource)
            : configField.name,
        visible: get('dataSource.visible.value', schemaData)
      };
      const format =
        isChangeFunction && changedOthersInfo && !isNil(changedOthersInfo.defaultFormat)
          ? changedOthersInfo.defaultFormat
          : getDataFormatValue();

      let cellColorSetting = get('color.value.settingValue.cellColor', dataFormattings);
      let textColorSetting = get('color.value.settingValue.textColor', dataFormattings);

      if (isChangeFunction || changedKey === FILED_OPTIONS.FormatKey) {
        resetSettingByRangeDataWhenChangeFuntionOrFormat();
      }

      const defaultAlternativeText = DEFAULT_PROPERTIES_VALUE.dataFormattings.alternativeText;

      let propertiesInfo = {
        fieldItemVisible: get('dataSource.fieldItemVisible.value', schemaData),
        dataFormattings: {
          format: format,
          font: {
            family: dataFormattingsFontFormat.fontFamily,
            size: dataFormattingsFontFormat.fontSize,
            bold: dataFormattingsFontStyle.fontBold,
            italic: dataFormattingsFontStyle.fontItalic,
            underline: dataFormattingsFontStyle.fontUnderline
          },
          width: {
            value: dataFormattingsWidth
          },
          alignment: get('alignment.value', dataFormattings),
          verticalAlignment: get('verticalAlignment.value', dataFormattings),
          wordWrap: get('columnWordWrap.value', dataFormattings),
          sort: get('sort.value', dataFormattings) || '',
          color: {
            textColor: textColorSetting,
            cellColor: cellColorSetting
          },
          alternativeText: {
            value: getOr(defaultAlternativeText.value, 'value', dataFormattingsAlternativeText),
            rangeValue: getOr(defaultAlternativeText.rangeValue, 'rangeValue', dataFormattingsAlternativeText),
            rangePercent: getOr(defaultAlternativeText.rangePercent, 'rangePercent', dataFormattingsAlternativeText)
          },
          customURL: {
            url: get('url', customURL),
            option: get('option', customURL),
            selectedIconValue: get('selectedIconValue', customURL),
            viewSettingByLink: get('viewSettingByLink', customURL)
          },
          embeddedJavascript: {
            script: get('script', embeddedJavascript),
            selectedIconValue: get('selectedIconValue', embeddedJavascript),
            viewSettingByLink: get('viewSettingByLink', embeddedJavascript)
          },
          subTotal: subTotalData,
          grandTotal: grandTotalData
        },
        headerFormating: {
          font: {
            family: get('fontFamily', headerFormatingFontFormat),
            size: get('fontSize', headerFormatingFontFormat),
            bold: get('fontBold', headerFormatingFontStyle),
            italic: get('fontItalic', headerFormatingFontStyle),
            underline: get('fontUnderline', headerFormatingFontStyle),
            color: get('textColor', headerFormatingColor),
            backgroundColor: get('backgroundColor', headerFormatingColor)
          },
          alignment: get('alignment.value', headerFormating),
          verticalAlignment: get('verticalAlignment.value', headerFormating),
          wordWrap: get('wordWrap.value', headerFormating),
          columnGroup: get('columnGroup.value', headerFormating),
          verticalHeader: get('verticalHeader.value', headerFormating),
          verticalHeaderHeight: {
            value: verticalHeaderHeight
          }
        },
        drillDown: {
          subReport: {
            selectedReport: subReportValue.selectedReport,
            style: subReportValue.style,
            reportPartUsed: subReportValue.reportPartUsed,
            reportFilter: subReportValue.reportFilter,
            mappingFields: subReportValue.gridData,
            selectedIconValue: subReportValue.selectedIconValue,
            viewSettingByLink: subReportValue.viewSettingByLink
          }
        },
        otherProps: otherProps
      };

      configField.updateData(fieldInfo, propertiesInfo);
      invokeArgs(
        'changePropertiesByField',
        [configField, propertiesInfo, changedKey],
        configReportPart.reportPartContent
      );

      Promise.resolve()
        .bind(this)
        .then(() => {
          let manualChange = true;
          const mappingSource = this.mappingSource;

          if (isChangeFunction) {
            // Remove filter details when change function to GROUP or GROUP DAYS OLD NAME
            const needRemoveAggregated =
              changedOthersInfo.name === FUNCTION_CONFIG.GROUP.NAME ||
              changedOthersInfo.name === FUNCTION_CONFIG.GROUP_DAYS_OLD.NAME;

            const baseUpdate = () => {
              // update field name alias by changing of function.
              configField.field &&
                configField.field.visible &&
                (schemaData.dataSource.fieldNameAlias.value = configField.name);
              let formatSchema = schemaData.dataFormattings.format;
              // reload format data source by changing of function.
              mappingSource.getDataFormats().then(result => {
                formatSchema.props.promiseProps.options = result;
              });
              // update format value
              formatSchema.value = configField.properties.dataFormattings.format.formatId;
              // update sort value if chart is linear type
              reportPartContent.type === REPORT_PART_TYPE.Chart &&
                reportPartUtils.isLinear(configField, reportPartContent.defaultOptionsByType) &&
                includes(configField, reportPartContent.labelsElements) &&
                reportPartContent.resetSortAscending(configField) &&
                (schemaData.dataFormattings.sort.value = SORT_TYPES.ASC);
              return null;
            };

            let afterUpdateFunction = constant(null);

            if (needRemoveAggregated) {
              reportFilters.removeAggregatedField(configField);
            } else {
              afterUpdateFunction = () => {
                const newAlias = configField.name;

                reportFilters.updateAggregatedFieldReportFieldAlias(oldAlias, newAlias, configReportPart.headerTitle);
              };
            }

            return reportPartUtils
              .setFunctionElement(
                reportPartContent,
                configField,
                omit('defaultFormat', changedOthersInfo) || {},
                manualChange
              )
              .then(afterUpdateFunction)
              .then(baseUpdate);
          }
          return null;
        })
        .then(() => {
          if (oldAlias !== configField.name) {
            configReportPart.processAfterChangeFieldNameAlias(oldAlias, configField.name);
          }

          // update html for form after a changed of field property.
          reportPartContent.updateHTMLCodeFieldContent && reportPartContent.updateHTMLCodeFieldContent(configField);

          const afterUpdateNeedPercentageData = invoke('needPercentageData', configField.properties);

          if (changedKey === 'subReport') {
            let reportDesignerStore = fieldStore.reportStore;

            reportDesignerStore.model.setIgnoreCheckChange();

            return reportDesignerStore.saveDraftReport(PAGE_NAME.FIELDS, true).then(() => {
              if (changedKey === 'subReport' && get('value.style', subreport) === DRIL_DOWN_STYLE.EMBEDDED) {
                fieldStore.validateFilterFieldsTypeSubReport().then(result => {
                  const error = getOr(null, 'messages[0]', result);
                  if(!isNil(error)) {
                    const commonError = localizer.getText('COMMON_MESSAGES_GENERIC_ERROR');
                    itemProps.confirmFn('Error', {
                      type: commonMessages.MESSAGE_TYPE.ERROR,
                      description: getOr(commonError, 'messages[0]', error),
                      showCloseIndicator: true
                    });
                  } else if (result) {
                    itemProps.confirmFn('Information', {
                      type: commonMessages.MESSAGE_TYPE.INFO_ACCEPT,
                      description: localizer.getText('REPORTDESIGNER_FILTER_LOGIC_SUBREPORT'),
                      showCancel: false
                    });
                  }
                });
              }
            });
          }

          if (reportPartContent.type === REPORT_PART_TYPE.Form) {
            return null;
          }

          if ((reportPartContent.type === REPORT_PART_TYPE.Map && changedKey == 'color') ||
            needUpdateChangeKey.indexOf(changedKey) >= 0 ||
            (beforeUpdateNeedPercentageData != afterUpdateNeedPercentageData && afterUpdateNeedPercentageData)
          ) {
            return configReportPart.shouldUpdatePreview && itemProps.fieldStore.needUpdatePreview(configReportPart);
          }

          return null;
        });

      /**
       * check if need to reset setting by range data like Text Color, Cell Color, Alternative Text
       * @method resetSettingByRangeDataWhenChangeFuntionOrFormat
       */
      function resetSettingByRangeDataWhenChangeFuntionOrFormat() {
        let needResetSettingByrange = false;
        const functionInfo = isChangeFunction
          ? omit('defaultFormat', changedOthersInfo)
          : getOr({}, 'properties.dataFormattings.functionInfo', configField);

        const originalDataType = get('field.dataFieldType', configField);

        const curentDataType = configField.previewDataTypeByFunction;
        const nextDataType = ReportPartElementUtils.previewDataTypeByFunction(functionInfo, originalDataType);

        needResetSettingByrange = curentDataType !== nextDataType;

        if (!needResetSettingByrange && nextDataType === DATA_TYPES.DATE_TIME) {
          // special case for date time
          const currentDateSetting = configField.dateTimeFormatData;
          const nextDateSetting = ReportPartElementUtils.getDateTimeFormatData(originalDataType, functionInfo, format);
          needResetSettingByrange =
            isNil(currentDateSetting) !== isNil(nextDateSetting) ||
            get('groupBy', currentDateSetting) !== get('groupBy', nextDateSetting);
        }

        if (needResetSettingByrange) {
          dataFormattingsAlternativeText = cloneDeep(settingByRangeDefaultObj);
          textColorSetting = cloneDeep(settingByRangeDefaultObj);
          cellColorSetting = assignWith(
            objValue => {
              return isUndefined(objValue) ? cloneDeep(settingByRangeDefaultObj) : null;
            },
            cloneDeep(settingByRangeDefaultObj),
            get('color.value.settingValue.cellColor', dataFormattings)
          );
        }
      }

      /**
       * Get data for data format: id and groupBy value
       * @return {[type]} [description]
       */
      function getDataFormatValue() {
        changedOthersInfo = changedOthersInfo || {};
        const formatDataType =
          isChangeFunction &&
          ReportPartElementUtils.previewDataTypeByFunction(changedOthersInfo, get('field.dataFieldType', configField));

        if (changedKey === FILED_OPTIONS.FormatKey) {
          if (!dataFormattings.format.value) {
            return {};
          }

          return {
            formatId: dataFormattings.format.value,
            format: changedOthersInfo.format || '',
            groupBy: changedOthersInfo.groupBy || null,
            formatDataType: changedOthersInfo.formatDataType,
            jsFormatString: changedOthersInfo.jsFormatString
          };
        } else if (isChangeFunction && formatDataType !== configField.previewDataType) {
          return {};
        }
        return undefined;
      }

      function switchToUseIconOptionInHyperlink() {
        if (customURL && customURL.url && customURL.viewSettingByLink) {
          customURL.viewSettingByLink = false;
          customURL.selectedIconValue = find(item => {
            return item.value != get('selectedIconValue.value', embeddedJavascript);
          }, ICON_TYPES);
        }
        if (embeddedJavascript && embeddedJavascript.script && embeddedJavascript.viewSettingByLink) {
          embeddedJavascript.viewSettingByLink = false;
          embeddedJavascript.selectedIconValue = find(item => {
            return item.value != get('selectedIconValue.value', customURL);
          }, ICON_TYPES);
        }
      }
    })
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.ReportPartFieldPropsSchema.js